import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreOutlined, EyeOutlined, EditOutlined, DeleteOutlined, UserOutlined, BarChartOutlined } from '@ant-design/icons';
import { useResponsive } from '../../../../hooks/useResponsive';
import * as S from './ClassCard.styles';

export interface ClassCardProps {
  classId: string | number;
  className: string;
  studentsCount: number;
  averageGrade: number;
  onViewDetails?: () => void;
  onEditClass?: () => void;
  onDeleteClass?: () => void;
  onClick?: () => void;
}

export const ClassCard: React.FC<ClassCardProps> = ({
  classId,
  className,
  studentsCount,
  averageGrade,
  onViewDetails,
  onEditClass,
  onDeleteClass,
  onClick
}) => {
  const navigate = useNavigate();
  const { mobileOnly } = useResponsive();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  
  // Check if any actions are provided
  const hasActions = onViewDetails || onEditClass || onDeleteClass;

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuAction = (action: (() => void) | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    if (action) action();
  };

  const handleCardClick = () => {
    // Don't navigate if we're in the middle of a long press
    if (isLongPressing) {
      setIsLongPressing(false);
      return;
    }
    
    if (onClick) {
      onClick();
    } else {
      // Default action: navigate to class page using class name
      // Encode the class name to handle special characters and spaces
      const encodedClassName = encodeURIComponent(className.replace(/\s+/g, '-').toLowerCase());
      navigate(`/class/${encodedClassName}?id=${classId}`);
    }
  };

  // Long press handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!mobileOnly || !hasActions) return;
    
    setIsLongPressing(false);
    longPressTimerRef.current = setTimeout(() => {
      setIsLongPressing(true);
      setIsMenuOpen(true);
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500); // 500ms for long press
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTouchMove = () => {
    // Cancel long press if user moves finger
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      setIsLongPressing(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const CardComponent = hasActions ? S.CardWithActions : S.Card;

  return (
    <S.CardWrapper
      onClick={handleCardClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      isLongPressing={isLongPressing}
    >
      <CardComponent>
        {/* Header with class name and actions menu */}
        <S.CardHeader>
          <S.ClassTitle>
            <S.ClassIcon>🏫</S.ClassIcon>
            {className}
          </S.ClassTitle>
          
          {hasActions && (
            <S.ActionsContainer ref={menuRef}>
              <S.ActionsButton onClick={handleMenuToggle}>
                <MoreOutlined />
              </S.ActionsButton>
              
              {isMenuOpen && (
                <S.ActionsMenu>
                  {onViewDetails && (
                    <S.MenuItem onClick={(e) => handleMenuAction(onViewDetails, e)}>
                      <EyeOutlined />
                      View Details
                    </S.MenuItem>
                  )}
                  {onEditClass && (
                    <S.MenuItem onClick={(e) => handleMenuAction(onEditClass, e)}>
                      <EditOutlined />
                      Edit Class
                    </S.MenuItem>
                  )}
                  {onDeleteClass && (
                    <S.MenuItemDanger onClick={(e) => handleMenuAction(onDeleteClass, e)}>
                      <DeleteOutlined />
                      Delete
                    </S.MenuItemDanger>
                  )}
                </S.ActionsMenu>
              )}
            </S.ActionsContainer>
          )}
        </S.CardHeader>

        {/* Stats section */}
        <S.StatsSection>
          <S.StatItem>
            <S.StatIcon>
              <UserOutlined />
            </S.StatIcon>
            <S.StatContent>
              <S.StatValue>{studentsCount}</S.StatValue>
              <S.StatLabel>Students</S.StatLabel>
            </S.StatContent>
          </S.StatItem>

          <S.StatItem>
            <S.GradeStatIcon>
              <BarChartOutlined />
            </S.GradeStatIcon>
            <S.StatContent>
              <S.StatValue>{Math.round(averageGrade) || 'N/A'}</S.StatValue>
              <S.StatLabel>Avg Grade</S.StatLabel>
            </S.StatContent>
          </S.StatItem>
        </S.StatsSection>

      </CardComponent>
    </S.CardWrapper>
  );
};
